import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Download, Search } from 'lucide-react';
import { SaleDetailSlideOver } from '@/components/sales/SaleDetailSlideOver.js';
import { listSales, type SaleListItem } from '@/lib/api/sales.js';
import { listUsers } from '@/lib/api/users.js';
import { exportToCsv } from '@/lib/csv-export.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';
import {
  paymentMethodEmoji,
  paymentMethodLabel,
} from '@/lib/payment-display.js';
import { cn } from '@/lib/utils.js';
import { Pagination } from '@/components/common/Pagination.js';

type DatePreset = 'today' | 'yesterday' | 'week' | 'month' | 'custom';

function localDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfWeek(d: Date): Date {
  const copy = new Date(d);
  const day = copy.getDay();
  copy.setDate(copy.getDate() - day);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function rangeForPreset(preset: DatePreset): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const today = localDateStr(now);
  if (preset === 'today') return { dateFrom: today, dateTo: today };
  if (preset === 'yesterday') {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    const s = localDateStr(y);
    return { dateFrom: s, dateTo: s };
  }
  if (preset === 'week') {
    return { dateFrom: localDateStr(startOfWeek(now)), dateTo: today };
  }
  if (preset === 'month') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { dateFrom: localDateStr(first), dateTo: today };
  }
  return { dateFrom: '', dateTo: '' };
}

function saleStatusBadge(sale: SaleListItem) {
  const balance = Number(sale.balanceDue);
  const total = Number(sale.total);
  const returnTotal = Number(sale.returnTotal ?? 0);
  const hasReturn = Boolean(sale.hasReturn) || returnTotal > 0;

  if (hasReturn && returnTotal >= total - 0.001) {
    return {
      label: sale.returnNumber ? `Returned ${sale.returnNumber}` : 'Returned',
      className: 'bg-slate-200 text-slate-700',
    };
  }
  if (hasReturn) {
    return { label: 'Partial Return', className: 'bg-amber-100 text-amber-800' };
  }
  if (balance > 0.001) {
    return { label: 'Balance Due', className: 'bg-amber-100 text-amber-800' };
  }
  return { label: 'Paid', className: 'bg-green-100 text-green-700' };
}

export function SalesHistoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDate = searchParams.get('date');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [preset, setPreset] = useState<DatePreset>(
    initialDate === 'today' || !initialDate ? 'today' : 'custom',
  );
  const [dateFrom, setDateFrom] = useState(() => rangeForPreset('today').dateFrom);
  const [dateTo, setDateTo] = useState(() => rangeForPreset('today').dateTo);
  const [method, setMethod] = useState('');
  const [staffId, setStaffId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedSaleId, setSelectedSaleId] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (initialDate === 'today') {
      const range = rangeForPreset('today');
      setPreset('today');
      setDateFrom(range.dateFrom);
      setDateTo(range.dateTo);
    }
  }, [initialDate]);

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: listUsers,
  });

  const salesQuery = useQuery({
    queryKey: ['sales', debouncedSearch, dateFrom, dateTo, method, staffId, statusFilter, page],
    queryFn: () =>
      listSales({
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
        ...(method ? { method } : {}),
        ...(staffId ? { staffId } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        page,
        pageSize: 25,
      }),
  });

  const rows = salesQuery.data?.data ?? [];
  const meta = salesQuery.data?.meta;
  const total = meta?.total ?? 0;
  const pageSize = meta?.pageSize ?? 25;

  const summary = useMemo(() => {
    const fromMeta = meta?.summary;
    if (fromMeta) {
      return {
        count: fromMeta.salesCount ?? meta?.total ?? rows.length,
        total: Number(fromMeta.totalRevenue ?? 0),
        cash: Number(fromMeta.cashTotal ?? 0),
        card: Number(fromMeta.cardTotal ?? 0),
      };
    }
    let cash = 0;
    let card = 0;
    let total = 0;
    for (const row of rows) {
      total += Number(row.total);
      const methods = row.paymentMethods ?? [];
      if (methods.includes('cash')) cash += Number(row.amountPaid ?? row.paidTotal ?? row.total);
      if (methods.includes('card')) card += Number(row.amountPaid ?? row.paidTotal ?? row.total);
    }
    return { count: meta?.total ?? rows.length, total, cash, card };
  }, [meta, rows]);

  function applyPreset(next: DatePreset) {
    setPreset(next);
    setPage(1);
    if (next === 'custom') return;
    const range = rangeForPreset(next);
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
    if (next === 'today') {
      setSearchParams({ date: 'today' }, { replace: true });
    } else if (searchParams.has('date')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('date');
      setSearchParams(nextParams, { replace: true });
    }
  }

  function handleExport() {
    if (!rows.length) return;
    exportToCsv(
      rows.map((row) => ({
        saleNumber: row.saleNumber ?? row.ticketNumber,
        customer: row.customerName ?? 'Walk-in',
        items: row.itemSummary ?? '',
        itemCount: row.itemCount,
        total: row.total,
        paid: row.amountPaid ?? row.paidTotal ?? '',
        balanceDue: row.balanceDue,
        methods: (row.paymentMethods ?? []).join('|'),
        status: saleStatusBadge(row).label,
        createdAt: row.createdAt,
        staff: row.createdBy
          ? `${row.createdBy.firstName} ${row.createdBy.lastName}`
          : '',
      })),
      `sales-history-${dateFrom || 'all'}-${dateTo || 'all'}.csv`,
    );
  }

  const presets: Array<{ id: DatePreset; label: string }> = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'week', label: 'This Week' },
    { id: 'month', label: 'This Month' },
    { id: 'custom', label: 'Custom Range' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-950">Sales History</h1>
          <p className="mt-1 text-sm text-slate-500">POS sales, payments, and returns</p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={rows.length === 0}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              placeholder="Search sale #, customer, item…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <select
            className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
            value={staffId}
            onChange={(e) => {
              setStaffId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Staff</option>
            {(usersQuery.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName}
              </option>
            ))}
          </select>
          <select
            className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
            value={method}
            onChange={(e) => {
              setMethod(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Methods</option>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="store_credit">Store Credit</option>
            <option value="check">Check</option>
            <option value="other">Other</option>
          </select>
          <select
            className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Status: All</option>
            <option value="paid">Paid</option>
            <option value="balance_due">Balance Due</option>
            <option value="returned">Returned</option>
            <option value="partial_return">Partial Return</option>
          </select>
        </div>

        <div className="flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPreset(p.id)}
              className={cn(
                'rounded-full px-3 py-1.5 text-xs font-semibold',
                preset === p.id
                  ? 'bg-primary text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>

        {preset === 'custom' ? (
          <div className="flex flex-wrap gap-3">
            <label className="text-sm text-slate-600">
              From{' '}
              <input
                type="date"
                className="ml-1 rounded-lg border border-slate-300 px-2 py-1.5"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
              />
            </label>
            <label className="text-sm text-slate-600">
              To{' '}
              <input
                type="date"
                className="ml-1 rounded-lg border border-slate-300 px-2 py-1.5"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
              />
            </label>
          </div>
        ) : null}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-primary/5 px-4 py-3 text-sm font-semibold text-slate-800">
        {summary.count} sales · Total: {formatCurrency(summary.total)}
        {summary.cash > 0 || summary.card > 0 ? (
          <>
            {' '}
            · Cash: {formatCurrency(summary.cash)} · Card: {formatCurrency(summary.card)}
          </>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3">Sale #</th>
              <th className="px-5 py-3">Customer</th>
              <th className="px-5 py-3">Items</th>
              <th className="px-5 py-3">Total</th>
              <th className="px-5 py-3">Method</th>
              <th className="px-5 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {salesQuery.isLoading ? (
              <tr>
                <td colSpan={6} className="px-5 py-10 text-center text-slate-500">
                  Loading sales…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-10 text-center text-slate-500">
                  No sales found for these filters.
                </td>
              </tr>
            ) : (
              rows.map((sale) => {
                const badge = saleStatusBadge(sale);
                const methods = sale.paymentMethods ?? [];
                return (
                  <tr key={sale.id} className="hover:bg-slate-50">
                    <td className="whitespace-nowrap px-5 py-4">
                      <div className="font-semibold text-slate-950">
                        {sale.saleNumber ?? sale.ticketNumber}
                      </div>
                      <div className="text-xs text-slate-500">{formatDateTime(sale.createdAt)}</div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-medium text-slate-900">
                        {sale.customerName ?? 'Walk-in'}
                      </div>
                      {sale.itemSummary ? (
                        <div className="mt-0.5 max-w-xs truncate text-xs text-slate-500">
                          {sale.itemSummary}
                        </div>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-slate-600">
                      {sale.itemCount} item{sale.itemCount === 1 ? '' : 's'}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4">
                      <div className="font-semibold text-slate-950">
                        {formatCurrency(sale.total)}
                      </div>
                      <span
                        className={cn(
                          'mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold',
                          badge.className,
                        )}
                      >
                        {badge.label}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-slate-700">
                      {methods.length === 0
                        ? '—'
                        : methods
                            .map((m) => `${paymentMethodEmoji(m)} ${paymentMethodLabel(m)}`)
                            .join(', ')}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-right">
                      <button
                        type="button"
                        className="text-sm font-semibold text-primary hover:underline"
                        onClick={() => setSelectedSaleId(sale.id)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />

      <SaleDetailSlideOver
        open={Boolean(selectedSaleId)}
        saleId={selectedSaleId}
        onClose={() => setSelectedSaleId(null)}
      />
    </div>
  );
}
